import { AnalyticsContext, UserContext } from '@/context';
import { useMutation, useQuery } from '@/hooks';
import { GET_CURRENT_USER, GET_VIEW_ONLY_SUPPORT } from '@/queries';
import { Menu, MenuItem, Popover, Position } from '@blueprintjs/core';
import React, { useContext } from 'react';

import { CLOSE_VIEW_ONLY_SUPPORT } from '@/mutations';
import authenticationService from '@/services/authentication';
import { AppToaster } from '@/utils/toaster';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const eyeSvgIcon = {
  prefix: 'scrtracker',
  iconName: 'eye',
  icon: [
    16,
    16,
    [],
    null,
    [
      'M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z',
      'M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z',
    ],
  ],
};

export const useViewOnlyMenu = () => {
  const user = useContext(UserContext);

  const isSupportAdmin = user?.isSupportAdmin;
  const hasViewOnlyEnabled = !!user?.viewOnlySupport?.open;
  const isViewOnlySession = authenticationService.isViewOnlyAccessEnabled();
  const isImpersonating = !!user?.support?.impersonating;

  const {
    data: tickets,
    loading,
    error,
    refetch,
  } = useQuery(GET_VIEW_ONLY_SUPPORT, {
    skip: !isSupportAdmin,
    selector: 'viewOnlySupport',
    pollInterval: 20000,
  });

  if (!user || loading || error) return null;

  // Support admin currently viewing as user (view-only impersonation session)
  if (isSupportAdmin && isImpersonating && isViewOnlySession) {
    return {
      component: StopViewOnlyMenu,
      componentProps: {},
      name: 'StopViewOnly',
      icon: eyeSvgIcon,
      color: 'yellow',
    };
  }

  // End-user (non-support-admin): show Eye icon only when enabled
  if (!isSupportAdmin) {
    if (!hasViewOnlyEnabled) return null;
    return {
      component: DisableViewOnlyMenu,
      componentProps: {},
      name: 'ViewOnly',
      icon: eyeSvgIcon,
      color: 'yellow',
    };
  }

  // Support admin: show list of view-only tickets
  if (!tickets?.length) return null;
  return {
    component: ViewOnlyMenu,
    componentProps: {
      tickets: tickets?.map((ticket) => ({
        id: ticket.id,
        userId: ticket.user.id,
        email: ticket.user.email,
        name: ticket.user.name,
        organisation: ticket.user.organisation,
      })),
      refetchTickets: refetch,
    },
    name: 'ViewOnly',
    icon: eyeSvgIcon,
    color: 'yellow',
  };
};

const StopViewOnlyMenu = ({ children }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Popover position={Position.BOTTOM}>
      {children}
      <Menu>
        <MenuItem
          text={t(`Stop view access`)}
          onClick={() => {
            authenticationService.setViewOnlyAccess(false);
            authenticationService.impersonate(null);
            navigate('/', { replace: true });
            window.location.reload();
          }}
        />
      </Menu>
    </Popover>
  );
};

const DisableViewOnlyMenu = ({ children }) => {
  const { t } = useTranslation();
  const [closeViewOnlySupport] = useMutation(CLOSE_VIEW_ONLY_SUPPORT, {});
  const [getCurrentUser] = useLazyQuery(GET_CURRENT_USER, {
    fetchPolicy: 'network-only',
  });

  return (
    <Popover position={Position.BOTTOM}>
      {children}
      <Menu>
        <MenuItem
          intent="warning"
          icon="disable"
          text={t('Disable view access')}
          onClick={async () => {
            await closeViewOnlySupport();
            AppToaster.show({
              intent: 'success',
              icon: 'tick',
              message: t('View-only access has been disabled.'),
            });
            await getCurrentUser();
          }}
        />
      </Menu>
    </Popover>
  );
};

const ViewOnlyMenu = ({ children, tickets, refetchTickets }) => {
  const analytics = useContext(AnalyticsContext);
  const [closeViewOnlySupport] = useMutation(CLOSE_VIEW_ONLY_SUPPORT, {});
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleViewOnly = async (ticketId) => {
    authenticationService.impersonate(ticketId);
    authenticationService.setViewOnlyAccess(true);
    navigate('/', { replace: true });
    window.location.reload();
  };

  const handleDisableViewOnly = async (userId) => {
    try {
      await closeViewOnlySupport({
        variables: {
          userId,
        },
      });
      analytics.events.users?.disableImpersonationButton?.({
        userId,
      });
      AppToaster.show({
        intent: 'success',
        icon: 'tick',
        message: t('View-only access has been disabled.'),
      });
      await refetchTickets();
    } catch (error) {
      AppToaster.show({
        intent: 'danger',
        icon: 'error',
        message: t('An error occurred while disabling view-only access.'),
      });
    }
  };

  return (
    <Popover position={Position.BOTTOM_RIGHT}>
      {children}
      <Menu>
        {tickets?.map((ticket) => (
          <>
            <MenuItem
              key={ticket.id}
              icon="user"
              text={
                <>
                  {ticket.organisation.federation ? (
                    <>
                      <b>{ticket.organisation.name}</b> ({t('part of')}{' '}
                      <b>{ticket.organisation.federation.name})</b>
                    </>
                  ) : (
                    <b>{ticket.organisation.name}</b>
                  )}
                  <div>
                    {ticket.name} - {ticket.email}
                  </div>
                </>
              }
              onClick={ticket.id ? () => handleViewOnly(ticket.id) : undefined}
              children={
                <>
                  <MenuItem
                    intent="primary"
                    icon="eye-open"
                    text={t('View user')}
                    onClick={
                      ticket.id ? () => handleViewOnly(ticket.id) : undefined
                    }
                  />
                  <MenuItem
                    intent="danger"
                    icon="remove"
                    text={t('Disable view access')}
                    onClick={
                      ticket.userId
                        ? () => handleDisableViewOnly(ticket.userId)
                        : undefined
                    }
                  />
                </>
              }
            />
          </>
        ))}
      </Menu>
    </Popover>
  );
};

export default ViewOnlyMenu;

